/* eslint-disable no-restricted-globals */
import { toPng } from 'html-to-image';
import pptxgen from 'pptxgenjs';
import { TFunction } from 'react-i18next';
import { ReportSubscriptionSubscription } from '../../generated/hooks';
import i18n from '../../translations/i18n';
import { wait } from '../../utils';
import { Filter, getTeamsById, getTeamsLabel } from '../reportHelper';
import { IReport } from './base';
import { getAnonymityModeKeyForShowingAnonymityInfo, theme } from '@arnold/common';

const formatAnonymityLine = (report: IReport, t: TFunction) => {
  const isPublic = (report as any).firstSurvey?.public ?? (report as any).surveys?.[0]?.public ?? false;
  if (isPublic) {
    return t('Report:anonymityMode.anonymous');
  }
  return t(
    `Report:anonymityMode.${getAnonymityModeKeyForShowingAnonymityInfo(
      report.anonymitySettings?.default ?? null,
      report.anonymitySettings?.allowedLevels ?? [],
    )}`,
  );
};

/*
  INFO FOR EVERYONE WHO WILL ADD NEW FEATURES OR SLIDES INTO THIS PRESENTATION:

  - you can easily open any example presentation from the ticket and click on any element with right mouse button and select size and position.
    This will open a window with exact position and size of the element. You can use this to position for the elements in this library.
  
  - keep in mind that this library works with inches, so you need to convert everything from cm to inches. We have a helper function for that called cmToInch.

  - any example presentation from ticket must have the wide layout (called "Widescreen" in PowerPoint). Otherwise, the elements will not be positioned correctly.
*/

interface ImageData {
  image: string;
  width: number;
  height: number;
}

/**
 * This function gathers all the data for the presentation and calls the generatePowerpoint function
 */
export const exportPowerpoint = async (
  globalFilters: Filter[],
  report: IReport,
  rootTeams: ReportSubscriptionSubscription['getReport']['teams'],
  sections: NonNullable<ReportSubscriptionSubscription['getReport']['reportDefinition']>['sections'],
  addedOnFrom: string | undefined | null,
  addedOnTo: string | undefined | null,
  t: TFunction,
) => {
  await hideUnwantedElements();
  const companyName = report.metadata?.organization.name || '';
  const surveyName = report.reportDefinition?.titleLocal || '';
  const surveyRangeStartDate = addedOnFrom ? new Date(addedOnFrom) : new Date(report.metadata?.surveyStart || 0);
  const surveyRangeEndDate = addedOnTo ? new Date(addedOnTo) : new Date(report.metadata?.surveyEnd || 0);
  const surveyEnded = surveyRangeEndDate! < new Date();

  // get teams for the first slide
  const teams = getTeamsLabel(rootTeams!, t);

  // get filters for the first slide
  const filters =
    globalFilters.length === 0
      ? ''
      : globalFilters
          .map((filter) => getFilterText(filter, report, t))
          .filter((s) => s)
          .join('; ');

  const summary = document.getElementById('report-conclusion');
  const conclusionCards = document.querySelectorAll('.data-conclusion-card-for-pptx') as NodeListOf<HTMLElement>;
  for (const card of conclusionCards) {
    card.style.setProperty('border', `1px solid ${theme.colors.borderSeparator.default}`, 'important');
  }
  await wait(20);
  const summaryImageData = await htmlToBase64Image(summary as HTMLElement);
  for (const card of conclusionCards) {
    card.style.removeProperty('border');
  }

  const surveyDateRange = `${
    surveyRangeEndDate ? '' : `${t('powerpointDateFrom')} `
  }${surveyRangeStartDate.toLocaleDateString(i18n.language)}${
    surveyRangeEndDate ? ` - ${surveyRangeEndDate.toLocaleDateString(i18n.language)}` : ''
  }`;
  const surveyDateRangeLong = `${
    surveyRangeEndDate ? '' : `${t('powerpointDateFrom')} `
  }${surveyRangeStartDate.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' })}${
    surveyRangeEndDate
      ? ` - ${surveyRangeEndDate.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' })}`
      : ''
  }`;
  const dateGenerationText = `${t('powerpointGeneratedOn')}: ${new Date().toLocaleString(i18n.language)}${
    !surveyEnded ? ` (${t('powerpointSurveyNotEnded')})` : ''
  }`;
  const surveyDetailsBottomText = `${surveyName}: ${surveyDateRange}`;

  const pres = new pptxgen();
  pres.layout = 'LAYOUT_WIDE';

  // First slide
  const firstSlide = pres.addSlide();
  firstSlide.background = { color: PRESENTATION_BLUE };
  addIntroOrOutroText(firstSlide, surveyName);
  addIntroMiddleText(firstSlide, companyName, surveyDateRangeLong, formatAnonymityLine(report, t), teams, filters, t);
  addLogo(firstSlide, true);
  addBottomRobot(firstSlide);
  addLeftBottomText(firstSlide, dateGenerationText, true);

  // Summary slide
  const summarySlide = pres.addSlide();
  addSummarySectionText(summarySlide, t('intro'));
  addScreenshot(summarySlide, summaryImageData);
  addLogo(summarySlide, false);
  addLeftBottomText(summarySlide, surveyDetailsBottomText, false);

  // AI summary slide
  const summaryDiv = document.querySelector('[data-complete-ai-report-summary="true"]');
  if (summaryDiv) {
    const summaryAIImageData = await htmlToBase64Image(summaryDiv as HTMLElement);
    const summaryAISlide = pres.addSlide();
    addQuestionSectionText(summaryAISlide, surveyName);
    addAiBadge(summaryAISlide);
    addQuestionText(summaryAISlide, t('powerpointAiSummary'), '', { x: cmToInch(3.53) });
    addScreenshot(summaryAISlide, summaryAIImageData, -0.5);
    addLogo(summaryAISlide, false);
    addLeftBottomText(summaryAISlide, surveyDetailsBottomText, false);
  }

  // Middle slides - traversing sections/widgets
  const alreadyDoneQuestionIndexes: string[] = [];
  for (const section of sections || []) {
    const sectionName = section.headingLocal || t('Report:results');
    if (section.type === 'pulser') {
      const scoreImage = document.querySelector('[data-pulser-total-score]') as HTMLElement;
      const questionGroup0 = document.querySelector('[data-pulser-question-group-index="0"]') as HTMLElement;
      const questionGroup1 = document.querySelector('[data-pulser-question-group-index="1"]') as HTMLElement;
      if (scoreImage && questionGroup0 && questionGroup1) {
        // Ensure all main questions are CLOSED before capturing total score and question groups
        const openHeadersForTotal = Array.from(
          document.querySelectorAll('[data-pulser-question-item-is-open="true"]'),
        ) as HTMLElement[];
        for (const header of openHeadersForTotal) {
          header.click();
          await wait(50);
        }
        if (openHeadersForTotal.length > 0) {
          await wait(200);
        }
        // Temporarily show the divider before capture
        let dividerSibling: HTMLElement | null = null;
        let cursor: Element | null = scoreImage.nextElementSibling;
        while (cursor) {
          if ((cursor as HTMLElement).hasAttribute?.('data-pptx-total-score-divider')) {
            dividerSibling = cursor as HTMLElement;
            break;
          }
          cursor = cursor.nextElementSibling;
        }
        if (!dividerSibling) {
          // Fallback: try global lookup
          dividerSibling = document.querySelector('[data-pptx-total-score-divider]') as HTMLElement | null;
        }
        if (dividerSibling) {
          dividerSibling.style.display = 'block';
          await wait(20);
        }
        const scoreImageChild = scoreImage.querySelector('[data-pulser-total-score-child]') as HTMLElement;
        const prevMarginBottom = scoreImageChild?.style.marginBottom;
        if (scoreImageChild) {
          scoreImageChild.style.setProperty('margin-bottom', '0px', 'important');
          await wait(20);
        }
        let scoreImageData = await htmlToBase64Image(scoreImage);
        if (scoreImageChild) {
          scoreImageChild.style.marginBottom = prevMarginBottom;
        }
        if (dividerSibling) {
          const dividerImageData = await htmlToBase64Image(dividerSibling);
          scoreImageData = await joinImagesVertically(scoreImageData, dividerImageData, 0);
          dividerSibling.style.display = 'none';
        }
        const questionGroupImages = await Promise.all([
          captureHeadersOnlyImage(questionGroup0),
          captureHeadersOnlyImage(questionGroup1),
        ]);
        // Total score slide
        const pulserTotalScoreSlide = pres.addSlide();
        addSummarySectionText(pulserTotalScoreSlide, sectionName);
        addScreenshotTop(pulserTotalScoreSlide, scoreImageData);
        addScreenshotBottomLeft(pulserTotalScoreSlide, questionGroupImages[0]);
        addScreenshotBottomRight(pulserTotalScoreSlide, questionGroupImages[1]);
        addLogo(pulserTotalScoreSlide, false);
        addLeftBottomText(pulserTotalScoreSlide, surveyDetailsBottomText, false);

        // Slides per pulser question and statement group page
        const questionHeaders = Array.from(
          document.querySelectorAll('[data-pulser-question-item-index]'),
        ) as HTMLElement[];
        for (const headerEl of questionHeaders) {
          // Ensure the section is open before capturing
          const isOpen = headerEl.getAttribute('data-pulser-question-item-is-open') === 'true';
          if (!isOpen) {
            (headerEl as HTMLElement).click();
            await wait(200);
          }
          // Find the wrapper that contains this question's statement groups
          let wrapper: HTMLElement | null = headerEl;
          while (wrapper && wrapper.parentElement && !wrapper.querySelector('[data-pulser-statement-group-index]')) {
            wrapper = wrapper.parentElement as HTMLElement;
          }
          if (!wrapper) {
            continue;
          }
          const statementGroups = Array.from(
            wrapper.querySelectorAll('[data-pulser-statement-group-index]'),
          ) as HTMLElement[];
          if (statementGroups.length === 0) {
            continue;
          }

          // Title base is the main section question text
          const titleBase = headerEl.querySelector('[data-pptx-id="report-intro-title"]')?.textContent?.trim() || '';

          // Pre-measure the max height across all statement-group pages to normalize aspect ratio between pages
          let maxPageHeight = 0;
          for (let i = 0; i < statementGroups.length; i++) {
            const groupsToHide = statementGroups.filter((_, idx) => idx !== i);
            const originalDisplays: Array<{ el: HTMLElement; display: string }> = [];
            for (const el of groupsToHide) {
              originalDisplays.push({ el, display: el.style.display });
              el.style.display = 'none';
            }
            await wait(10);
            maxPageHeight = Math.max(maxPageHeight, (wrapper as HTMLElement).getBoundingClientRect().height);
            for (const { el, display } of originalDisplays) {
              el.style.display = display;
            }
            await wait(10);
          }

          // Screenshot the whole wrapper while hiding other statement groups by index
          for (let i = 0; i < statementGroups.length; i++) {
            const groupsToHide = statementGroups.filter((_, idx) => idx !== i);

            // Freeze wrapper dimensions for consistent capture
            const prevWidth = (wrapper as HTMLElement).style.width;
            const prevMaxWidth = (wrapper as HTMLElement).style.maxWidth;
            const prevMinHeight = (wrapper as HTMLElement).style.minHeight;
            const stableWidth = (wrapper as HTMLElement).clientWidth;
            (wrapper as HTMLElement).style.width = `${stableWidth}px`;
            (wrapper as HTMLElement).style.maxWidth = `${stableWidth}px`;
            if (maxPageHeight > 0) {
              (wrapper as HTMLElement).style.minHeight = `${Math.ceil(maxPageHeight)}px`;
            }

            // Hide other groups
            const originalDisplays: Array<{ el: HTMLElement; display: string }> = [];
            for (const el of groupsToHide) {
              originalDisplays.push({ el, display: el.style.display });
              el.style.display = 'none';
            }

            // Small delay to ensure layout settles
            await wait(10);

            const fullImage = await htmlToBase64Image(wrapper as HTMLElement);
            const slideTitle = `${titleBase} ${i + 1}/${statementGroups.length}`;

            // Create slide now
            const slide = pres.addSlide();
            addSummarySectionText(slide, slideTitle);
            addScreenshotLarge(slide, fullImage);
            addLogo(slide, false);
            addLeftBottomText(slide, surveyDetailsBottomText, false);

            // Restore hidden groups and wrapper styles
            for (const { el, display } of originalDisplays) {
              el.style.display = display;
            }
            (wrapper as HTMLElement).style.width = prevWidth;
            (wrapper as HTMLElement).style.maxWidth = prevMaxWidth;
            (wrapper as HTMLElement).style.minHeight = prevMinHeight;

            await wait(10);
          }
          if (!isOpen) {
            // If we opened it before, we need to close it again
            (headerEl as HTMLElement).click();
            await wait(200);
          }
        }
      }
      continue; // Pulser is handled above
    }
    for (const widget of section.widgets || []) {
      if (
        widget.type !== 'STACKED_CHART' &&
        widget.type !== 'COMMENT_TABLE' &&
        widget.type !== 'SCALE_CHART' &&
        widget.type !== 'NPS'
      ) {
        continue;
      }
      const questionIndex = widget.questionDefinitionIndex;
      if (questionIndex == null) {
        throw new Error(`Question index on widget is null`);
      }
      if (alreadyDoneQuestionIndexes.includes(questionIndex)) {
        continue;
      }
      alreadyDoneQuestionIndexes.push(questionIndex);
      if (widget.type === 'NPS') {
        const npsElement = document.querySelector('[data-nps-widget]') as HTMLElement;
        if (npsElement) {
          const npsIsClosed = npsElement.getAttribute('data-nps-widget-is-open') !== 'true';
          if (npsIsClosed) {
            const toggleButton = npsElement.querySelector('[data-nps-widget-toggle-button]') as HTMLElement | null;
            if (toggleButton) {
              toggleButton.click();
              await wait(250);
            }
          }
          const npsContentElements = npsElement.querySelectorAll(
            '[data-content-box-card], [data-content-box-header-wrapper]',
          );
          const elementRestores: Array<{
            element: HTMLElement;
            marginBottom: string;
            marginTop: string;
            boxShadow: string;
          }> = [];
          for (const element of npsContentElements) {
            const el = element as HTMLElement;
            elementRestores.push({
              element: el,
              marginBottom: el.style.marginBottom,
              marginTop: el.style.marginTop,
              boxShadow: el.style.boxShadow,
            });
            el.style.setProperty('margin-bottom', '0px', 'important');
            el.style.setProperty('margin-top', '0px', 'important');
            el.style.setProperty('box-shadow', 'none', 'important');
          }
          if (elementRestores.length > 0) {
            await wait(20);
          }
          const npsImageData = await htmlToBase64Image(npsElement);
          for (const { element, marginBottom, marginTop, boxShadow } of elementRestores) {
            element.style.marginBottom = marginBottom || '';
            element.style.marginTop = marginTop || '';
            element.style.boxShadow = boxShadow || '';
          }
          if (npsImageData) {
            const npsSlide = pres.addSlide();
            addSummarySectionText(npsSlide, t('NPS'));
            addScreenshotLarge(npsSlide, npsImageData);
            addLogo(npsSlide, false);
            addLeftBottomText(npsSlide, surveyDetailsBottomText, false);
          }
          if (npsIsClosed) {
            // If we opened it before, we need to close it again
            const toggleButton = npsElement.querySelector('[data-nps-widget-toggle-button]') as HTMLElement | null;
            if (toggleButton) {
              toggleButton.click();
              await wait(50);
            }
          }
        }
        continue;
      }
      const headerElement = document.querySelector(`[data-text-question-index="${questionIndex}"]`);
      if (headerElement == null) {
        continue; // Some questions are not present in the report - it's ok, we do not want these questions in the presentation
      }
      const questionTextElement = headerElement.querySelector('[data-for-query-selector-question-text]');
      const questionDescriptionElement = headerElement.querySelector('[data-for-query-selector-question-description]');
      if (questionTextElement == null || questionDescriptionElement == null) {
        throw new Error(
          `Question text or description element of chart with question index ${questionIndex} was not found`,
        );
      }
      let legendToScreenshot: Element | null | undefined = null;
      let aiKeywords = false;
      let moreThan9Comments = false;
      if (widget.type === 'STACKED_CHART') {
        const elementToScreenshot = document.querySelector(
          `[data-report-middle-section="true"] [data-chart-question-index="${questionIndex}"] [data-for-query-selector-stacked-chart]`,
        );
        legendToScreenshot = document.querySelector(
          `[data-chart-question-index="${questionIndex}"] [data-cy="compare-teams-select"]`,
        )?.parentElement?.parentElement;
        if (!elementToScreenshot) continue;
        const imageData = await htmlToBase64Image(elementToScreenshot as HTMLElement);
        let legendImageData: ImageData | null = null;
        if (legendToScreenshot) {
          await wait(10);
          legendImageData = await htmlToBase64Image(legendToScreenshot as HTMLElement);
        }
        const slide = pres.addSlide();
        slide.background = { color: 'FFFFFF' };
        addQuestionSectionText(slide, sectionName);
        const description = `${questionDescriptionElement.childNodes[0].textContent || ''} ${
          aiKeywords ? t('powerpointAiKeywordsDisclosure') : ''
        }${moreThan9Comments ? t('powerpointMoreThan9CommentsDisclosure') : ''}`;
        addQuestionText(slide, questionTextElement.childNodes[0].textContent || '', description, undefined, aiKeywords);
        if (legendImageData) {
          const legendAndChartImageData = await joinImagesVertically(legendImageData, imageData, 10);
          addScreenshot(slide, legendAndChartImageData);
        } else {
          addScreenshot(slide, imageData);
        }
        addLogo(slide, false);
        addLeftBottomText(slide, surveyDetailsBottomText, false);
        await wait(10);
      } else if (widget.type === 'COMMENT_TABLE') {
        let elementToScreenshot: Element | null = document.querySelector(
          `[data-comment-box-question-index="${questionIndex}"] [data-for-query-selector-ai-keywords]`,
        );
        if (elementToScreenshot) {
          aiKeywords = true;
        } else {
          elementToScreenshot = document.querySelector(
            `[data-comment-box-question-index="${questionIndex}"] [data-for-query-selector-comments]`,
          );
          moreThan9Comments = !!document.querySelector(
            `[data-comment-box-question-index="${questionIndex}"] [data-cy="report-comments-load-more"]`,
          );
        }
        if (!elementToScreenshot) continue;
        const imageData = await htmlToBase64Image(elementToScreenshot as HTMLElement);
        const slide = pres.addSlide();
        slide.background = { color: 'FFFFFF' };
        addQuestionSectionText(slide, sectionName);
        const description = `${questionDescriptionElement.childNodes[0].textContent || ''} ${
          aiKeywords ? t('powerpointAiKeywordsDisclosure') : ''
        }${moreThan9Comments ? t('powerpointMoreThan9CommentsDisclosure') : ''}`;
        addQuestionText(slide, questionTextElement.childNodes[0].textContent || '', description, undefined, aiKeywords);
        addScreenshot(slide, imageData);
        addLogo(slide, false);
        addLeftBottomText(slide, surveyDetailsBottomText, false);
        await wait(10);
      } else if (widget.type === 'SCALE_CHART') {
        const unsortedPages = Array.from(
          document.querySelectorAll(
            `[data-chart-question-index="${questionIndex}"] [data-for-query-selector-scale-chart]`,
          ),
        );
        const scaleChartPages: Element[] = [];
        const pageMap = new Map<number, Element>();
        unsortedPages.forEach((page) => {
          const pageNum = parseInt(page.getAttribute('data-scale-chart-page') || '0', 10);
          pageMap.set(pageNum, page);
        });
        for (let i = 1; i <= pageMap.size; i++) {
          const page = pageMap.get(i);
          if (page) scaleChartPages.push(page);
        }
        for (let i = 0; i < scaleChartPages.length; i++) {
          const page = scaleChartPages[i];
          const pageNumber = page.getAttribute('data-scale-chart-page');
          const totalPages = page.getAttribute('data-scale-chart-total-pages');
          const baseQuestionText =
            page.getAttribute('data-scale-chart-question-text') || questionTextElement.childNodes[0]?.textContent || '';
          const pageImageData = await htmlToBase64Image(page as HTMLElement);
          let pageLegendImageData: ImageData | null = null;
          if (i === 0 && legendToScreenshot) {
            await wait(10);
            pageLegendImageData = await htmlToBase64Image(legendToScreenshot as HTMLElement);
          }
          const slide = pres.addSlide();
          slide.background = { color: 'FFFFFF' };
          addQuestionSectionText(slide, sectionName);
          addQuestionText(
            slide,
            `${baseQuestionText} ${totalPages && totalPages !== '1' ? `(${pageNumber}/${totalPages})` : ''}`,
            questionDescriptionElement.childNodes[0]?.textContent || '',
          );
          if (pageLegendImageData) {
            const legendAndChartImageData = await joinImagesVertically(pageLegendImageData, pageImageData, 10);
            addScreenshot(slide, legendAndChartImageData);
          } else {
            addScreenshot(slide, pageImageData);
          }
          addLogo(slide, false);
          addLeftBottomText(slide, surveyDetailsBottomText, false);
          await wait(10);
        }
      }
    }
  }

  // Last slide
  const lastSlide = pres.addSlide();
  lastSlide.background = { color: PRESENTATION_BLUE };
  addIntroOrOutroText(lastSlide, t('powerpointLastSlideText'));
  addCenterRobot(lastSlide);

  // Save the Presentation
  pres.writeFile({ fileName: surveyName + '.pptx' });
};

const PRESENTATION_BLUE = '#0177CC';

const getFilterText = (filter: Filter, report: IReport, t: TFunction) => {
  switch (filter.operator) {
    case 'organization-level':
      return t('powerpointTeamComparisonLevel', { level: filter.values?.[0] || 1 });
    case 'unit':
    case 'team':
    case 'unit-teams': {
      const rawValues = filter.values ?? [];
      const teamIds = rawValues
        .filter((value): value is string => Boolean(value))
        // remove team_ or unit_ prefix from the value so just the id will remain
        .map((value) => value.replace(/^team_|^unit_/, ''));
      const selectedTeams = getTeamsById(report.teams ?? [], teamIds);
      const teamsLabel = getTeamsLabel(selectedTeams, t);
      if (teamsLabel.length > 300) {
        return teamsLabel.slice(0, 300) + '…';
      }

      return teamsLabel;
    }
    case 'response':
      if (!filter.values) {
        return;
      }
      const question = report.questions?.find((q) => q.index === filter.questionDefinitionIndex);
      if (!question || !question.answersLocal) {
        return;
      }
      const questionText = question.text.replaceAll('\n', '').trim();
      const answers = filter.values
        .filter((v) => v != null)
        .map((a) => question.answersLocal![Number(a)])
        .join(', ');
      return `${questionText}: ${answers}`;
    default:
      return;
  }
};

// cm to inch
const cmToInch = (cm: number) => cm / 2.54;

const addLogo = (slide: pptxgen.Slide, white: boolean) => {
  slide.addImage({
    path: `${location.origin}/imagesForPresentations/arnoldLogo${white ? 'White' : 'Black'}.png`,
    x: cmToInch(25.46),
    y: cmToInch(16.94),
    w: cmToInch(6.65),
    h: cmToInch(1.67),
  });
};

const addBottomRobot = (slide: pptxgen.Slide) => {
  slide.addImage({
    path: `${location.origin}/imagesForPresentations/robotBottom.png`,
    x: cmToInch(12.69),
    y: cmToInch(15.15),
    w: cmToInch(8.47),
    h: cmToInch(3.91),
  });
};

const addCenterRobot = (slide: pptxgen.Slide) => {
  slide.addImage({
    path: `${location.origin}/imagesForPresentations/robotCenter.png`,
    x: cmToInch(9.06),
    y: cmToInch(5.64),
    w: cmToInch(15.74),
    h: cmToInch(12.54),
  });
};

const addAiBadge = (slide: pptxgen.Slide, imageOptions?: pptxgen.ImageProps) => {
  slide.addImage({
    path: `${location.origin}/imagesForPresentations/aiBadge.png`,
    x: cmToInch(2.58),
    y: cmToInch(3.36),
    w: cmToInch(1),
    h: cmToInch(0.66),
    ...imageOptions,
  });
};

const addIntroOrOutroText = (slide: pptxgen.Slide, text: string) => {
  slide.addText(text, {
    x: cmToInch(4.23),
    y: cmToInch(3.12),
    w: cmToInch(25.4),
    h: cmToInch(3.16),
    color: 'FFFFFF',
    fontSize: 49,
    fontFace: 'Roboto Black',
    bold: true,
    align: 'center',
  });
};

const addIntroMiddleText = (
  slide: pptxgen.Slide,
  companyName: string,
  surveyDateRange: string,
  anonymityMode: string,
  teams: string,
  filters: string,
  t: TFunction,
) => {
  if (teams.length > 300) {
    teams = teams.slice(0, 300) + '…';
  }
  if (filters.length > 600) {
    filters = filters.slice(0, 600) + '…';
  }
  const textNodes: PptxGenJS.default.TextProps[] = [
    {
      text: companyName + '\n' + surveyDateRange + '\n\n',
      options: {
        fontSize: 16,
        bold: true,
        lineSpacingMultiple: 1.5,
      },
    },
    {
      text: anonymityMode + '\n\n' + teams + '\n\n',
    },
  ];
  if (filters.length > 0) {
    textNodes.push(
      {
        text: t('powerpointUsedFilters') + ':\n',
        options: {
          bold: true,
        },
      },
      {
        text: filters,
      },
    );
  }

  slide.addText(textNodes, {
    x: cmToInch(4.23),
    y: cmToInch(7.06),
    w: cmToInch(25.4),
    h: cmToInch(6.36),
    color: 'FFFFFF',
    fontSize: 12,
    fontFace: 'Roboto',
    bold: false,
    align: 'center',
  });
};

const addLeftBottomText = (slide: pptxgen.Slide, text: string, white: boolean) => {
  slide.addText(text, {
    x: cmToInch(0.96),
    y: cmToInch(17.08),
    w: cmToInch(23.91),
    h: cmToInch(1.15),
    color: white ? 'FFFFFF' : PRESENTATION_BLUE,
    fontSize: 11,
    fontFace: 'Roboto',
    bold: true,
    align: 'left',
  });
};

const addSlideTitle = (slide: pptxgen.Slide, text: string, fontSize: number) => {
  slide.addText(text, {
    x: cmToInch(2.01),
    y: cmToInch(1.01),
    w: cmToInch(29.53),
    h: cmToInch(2.28),
    color: PRESENTATION_BLUE,
    fontSize,
    fontFace: 'Roboto',
    bold: true,
    align: 'left',
  });
};

const addSummarySectionText = (slide: pptxgen.Slide, text: string) => {
  addSlideTitle(slide, text, 32);
};

const addQuestionSectionText = (slide: pptxgen.Slide, text: string) => {
  addSlideTitle(slide, text, 20);
};

const addQuestionText = (
  slide: pptxgen.Slide,
  questionText: string,
  questionDescription: string,
  textOptions?: pptxgen.TextPropsOptions,
  aiIcon?: boolean,
) => {
  questionDescription = questionDescription.replaceAll('\n', '').trim();
  if (aiIcon) {
    // Adding spaces is not ideal, but it is the best solution, coz we need to keep it in the same paragraph as question text and we can't do it any other way
    questionDescription = `            ${questionDescription}`;
    addAiBadge(slide, { x: cmToInch(2.54), y: cmToInch(4.18) });
  }
  const textNodes: PptxGenJS.default.TextProps[] = [
    {
      text: questionText.replaceAll('\n', '').trim() + '\n',
      options: {
        fontSize: 14,
        bold: true,
      },
    },
    {
      text: questionDescription,
    },
  ];
  slide.addText(textNodes, {
    x: cmToInch(2.32),
    y: cmToInch(2.86),
    w: cmToInch(29.22),
    h: cmToInch(2.35),
    color: '000000',
    fontSize: 11,
    fontFace: 'Roboto',
    bold: false,
    align: 'left',
    lineSpacingMultiple: 1.5,
    ...textOptions,
  });
};

const addScreenshot = (
  slide: pptxgen.Slide,
  imageData: {
    image: string;
    width: number;
    height: number;
  },
  verticalOffset = 0,
) => {
  const MAX_HEIGHT = 12;
  let chartWidth = 27.38;
  const ratio = imageData.width / imageData.height;
  let height = chartWidth / ratio;
  if (height > MAX_HEIGHT) {
    // proportionally reduce the height and width to match the MAX_HEIGHT
    height = MAX_HEIGHT;
    chartWidth = height * ratio;
  }
  const centerX = (34 - chartWidth) / 2;
  slide.addImage({
    data: imageData.image,
    x: cmToInch(centerX),
    y: cmToInch(8 - height / 4 + verticalOffset),
    w: cmToInch(chartWidth),
    h: cmToInch(height),

    sizing: { type: 'contain', w: null!, h: null! },
  });
};

const addScreenshotTop = (slide: pptxgen.Slide, imageData: { image: string; width: number; height: number }) => {
  const MAX_HEIGHT = 7;
  let chartWidth = 25;
  const ratio = imageData.width / imageData.height;
  let height = chartWidth / ratio;
  if (height > MAX_HEIGHT) {
    height = MAX_HEIGHT;
    chartWidth = height * ratio;
  }
  const centerX = (34 - chartWidth) / 2;
  slide.addImage({
    data: imageData.image,
    x: cmToInch(centerX),
    y: cmToInch(3),
    w: cmToInch(chartWidth),
    h: cmToInch(height),
  });
};

const addScreenshotBottomLeft = (slide: pptxgen.Slide, imageData: { image: string; width: number; height: number }) => {
  const MAX_HEIGHT = 9;
  const MAX_WIDTH = 15.5;
  const ratio = imageData.width / imageData.height;

  let chartWidth = Math.min(MAX_WIDTH, 24);
  let height = chartWidth / ratio;
  if (height > MAX_HEIGHT) {
    height = MAX_HEIGHT;
    chartWidth = Math.min(MAX_WIDTH, height * ratio);
  }

  slide.addImage({
    data: imageData.image,
    x: cmToInch(1.5),
    y: cmToInch(7),
    w: cmToInch(chartWidth),
    h: cmToInch(height),
  });
};

const addScreenshotBottomRight = (
  slide: pptxgen.Slide,
  imageData: { image: string; width: number; height: number },
) => {
  const MAX_HEIGHT = 9;
  const MAX_WIDTH = 15.5;
  const ratio = imageData.width / imageData.height;
  let chartWidth = Math.min(MAX_WIDTH, 24);
  let height = chartWidth / ratio;
  if (height > MAX_HEIGHT) {
    height = MAX_HEIGHT;
    chartWidth = Math.min(MAX_WIDTH, height * ratio);
  }

  slide.addImage({
    data: imageData.image,
    x: cmToInch(34 - chartWidth - 1),
    y: cmToInch(7),
    w: cmToInch(chartWidth),
    h: cmToInch(height),
  });
};

// Larger rendering
const addScreenshotLarge = (slide: pptxgen.Slide, imageData: { image: string; width: number; height: number }) => {
  const MAX_HEIGHT = 15;
  let chartWidth = 28;
  const ratio = imageData.width / imageData.height;
  let height = chartWidth / ratio;
  if (height > MAX_HEIGHT) {
    height = MAX_HEIGHT;
    chartWidth = height * ratio;
  }
  const centerX = (34 - chartWidth) / 2;
  slide.addImage({
    data: imageData.image,
    x: cmToInch(centerX),
    y: cmToInch(3),
    w: cmToInch(chartWidth),
    h: cmToInch(height),
  });
};

// (getDataForPulser/getDataForNps were inlined into exportPowerpoint)

// Capture only the question headers (without their outer wrappers with borders) for a pulser column
const captureHeadersOnlyImage = async (groupEl: HTMLElement) => {
  const children = Array.from(groupEl.children) as HTMLElement[];
  const previousDisplays = children.map((c) => c.style.display);
  for (const c of children) c.style.display = 'none';

  const temp = document.createElement('div');
  const cs = getComputedStyle(groupEl);
  temp.style.boxSizing = 'border-box';
  temp.style.width = groupEl.clientWidth + 'px';
  temp.style.paddingLeft = cs.paddingLeft;
  temp.style.paddingRight = cs.paddingRight;
  temp.style.paddingTop = cs.paddingTop;
  temp.style.paddingBottom = cs.paddingBottom;

  const headers = Array.from(groupEl.querySelectorAll('[data-pulser-question-item-index]')) as HTMLElement[];
  for (const h of headers) {
    temp.appendChild(h.cloneNode(true));
  }
  groupEl.appendChild(temp);

  const img = await htmlToBase64Image(groupEl);

  groupEl.removeChild(temp);
  children.forEach((c, i) => (c.style.display = previousDisplays[i]));

  return img;
};

const htmlToBase64Image = async (element: HTMLElement) => {
  // Temporarily remove border-bottom for nodes explicitly marked for PPT export
  const nodesToToggle = Array.from(
    (element.matches?.('[data-pptx-no-border-bottom]') ? [element] : []).concat(
      Array.from(element.querySelectorAll('[data-pptx-no-border-bottom]')) as HTMLElement[],
    ),
  ) as HTMLElement[];
  const restores: Array<() => void> = [];
  for (const n of nodesToToggle) {
    const prevWidth = n.style.borderBottomWidth;
    const prevStyle = n.style.borderBottomStyle;
    const prevColor = n.style.borderBottomColor;
    n.style.setProperty('border-bottom-width', '0px', 'important');
    n.style.setProperty('border-bottom-style', 'none', 'important');
    n.style.setProperty('border-bottom-color', 'transparent', 'important');
    restores.push(() => {
      n.style.borderBottomWidth = prevWidth;
      n.style.borderBottomStyle = prevStyle;
      n.style.borderBottomColor = prevColor;
    });
  }

  const nodesToNoBreak = Array.from(element.querySelectorAll('[data-no-break]')) as HTMLElement[];
  for (const n of nodesToNoBreak) {
    const prevWhiteSpace = n.style.getPropertyValue('white-space');
    const prevWhiteSpacePriority = n.style.getPropertyPriority('white-space');
    const prevDisplay = n.style.getPropertyValue('display');
    const prevDisplayPriority = n.style.getPropertyPriority('display');
    n.style.setProperty('white-space', 'nowrap', 'important');
    n.style.setProperty('display', 'inline-block', 'important');
    restores.push(() => {
      if (prevWhiteSpace) n.style.setProperty('white-space', prevWhiteSpace, prevWhiteSpacePriority);
      else n.style.removeProperty('white-space');
      if (prevDisplay) n.style.setProperty('display', prevDisplay, prevDisplayPriority);
      else n.style.removeProperty('display');
    });
  }

  const nodesToFlexColumn = Array.from(
    (element.classList?.contains('data-make-flex-column') ? [element] : []).concat(
      Array.from(element.querySelectorAll('.data-make-flex-column')) as HTMLElement[],
    ),
  ) as HTMLElement[];
  for (const n of nodesToFlexColumn) {
    const prevFlexDir = n.style.getPropertyValue('flex-direction');
    const prevFlexDirPriority = n.style.getPropertyPriority('flex-direction');
    n.style.setProperty('flex-direction', 'column', 'important');
    restores.push(() => {
      if (prevFlexDir) n.style.setProperty('flex-direction', prevFlexDir, prevFlexDirPriority);
      else n.style.removeProperty('flex-direction');
    });
  }

  const nodesToNextSiblingAlignCenter = Array.from(
    element.querySelectorAll('[data-make-next-sibling-self-align-center]'),
  ) as HTMLElement[];
  for (const n of nodesToNextSiblingAlignCenter) {
    const nextSibling = n.nextElementSibling as HTMLElement | null;
    if (!nextSibling) continue;
    const prevAlignSelf = nextSibling.style.getPropertyValue('align-self');
    const prevAlignSelfPriority = nextSibling.style.getPropertyPriority('align-self');
    nextSibling.style.setProperty('align-self', 'center', 'important');
    restores.push(() => {
      if (prevAlignSelf) nextSibling.style.setProperty('align-self', prevAlignSelf, prevAlignSelfPriority);
      else nextSibling.style.removeProperty('align-self');
    });
  }

  const nodesToJustifySelfCenter = Array.from(
    element.querySelectorAll('[data-make-justify-self-center]'),
  ) as HTMLElement[];
  for (const n of nodesToJustifySelfCenter) {
    const prevJustifySelf = n.style.getPropertyValue('justify-self');
    const prevJustifySelfPriority = n.style.getPropertyPriority('justify-self');
    const prevWidth = n.style.getPropertyValue('width');
    const prevWidthPriority = n.style.getPropertyPriority('width');
    const prevMarginLeft = n.style.getPropertyValue('margin-left');
    const prevMarginLeftPriority = n.style.getPropertyPriority('margin-left');
    const prevMarginRight = n.style.getPropertyValue('margin-right');
    const prevMarginRightPriority = n.style.getPropertyPriority('margin-right');

    n.style.setProperty('justify-self', 'center', 'important');
    // `justify-self` only affects grid items; this fallback works in most layouts.
    n.style.setProperty('width', 'fit-content', 'important');
    n.style.setProperty('margin-left', 'auto', 'important');
    n.style.setProperty('margin-right', 'auto', 'important');
    restores.push(() => {
      if (prevJustifySelf) n.style.setProperty('justify-self', prevJustifySelf, prevJustifySelfPriority);
      else n.style.removeProperty('justify-self');
      if (prevWidth) n.style.setProperty('width', prevWidth, prevWidthPriority);
      else n.style.removeProperty('width');
      if (prevMarginLeft) n.style.setProperty('margin-left', prevMarginLeft, prevMarginLeftPriority);
      else n.style.removeProperty('margin-left');
      if (prevMarginRight) n.style.setProperty('margin-right', prevMarginRight, prevMarginRightPriority);
      else n.style.removeProperty('margin-right');
    });
  }

  const nodesToRemoveMaxWidth = Array.from(element.querySelectorAll('[data-remove-max-width]')) as HTMLElement[];
  for (const n of nodesToRemoveMaxWidth) {
    const prevMaxWidth = n.style.getPropertyValue('max-width');
    const prevMaxWidthPriority = n.style.getPropertyPriority('max-width');
    n.style.setProperty('max-width', 'none', 'important');
    restores.push(() => {
      if (prevMaxWidth) n.style.setProperty('max-width', prevMaxWidth, prevMaxWidthPriority);
      else n.style.removeProperty('max-width');
    });
  }

  await wait(20);

  const image = await toPng(element, {
    backgroundColor: 'transparent',
    skipAutoScale: true,
    skipFonts: true,
    pixelRatio: 4,
    filter(domNode) {
      if (
        domNode.style &&
        (domNode.getAttribute?.('data-pptx-id') === 'report-intro-title' ||
          domNode.parentElement?.className?.includes?.('multiValue'))
      ) {
        domNode.style.fontFamily =
          "Roboto, Arial, sans-serif, 'Open Sans', Calibri, 'Noto Sans', Verdana, 'Segoe UI', Tahoma, 'Trebuchet MS', 'DejaVu Sans', 'Liberation Sans', 'PT Sans', 'Fira Sans', Ubuntu";
        setTimeout(() => {
          domNode.style.removeProperty('font-family');
        }, 4000);
      }
      if (domNode.style && domNode.className?.includes?.('multiValue')) {
        const initialOverflow = domNode.style.overflow;
        domNode.style.overflow = 'visible';
        setTimeout(() => {
          domNode.style.overflow = initialOverflow;
        }, 4000);
      }
      if (domNode.style && domNode.parentElement?.className?.includes?.('multiValue')) {
        const initialOverflow = domNode.style.overflow;
        const initialTextOverflow = domNode.style.textOverflow;
        domNode.style.overflow = 'visible';
        domNode.style.textOverflow = 'clip';
        const nodeWidth = domNode.clientWidth;
        // this is to counter weird behavior that somehow makes the text slightly larger when screen shot is taken
        const rightPadding = nodeWidth > 0 ? Math.ceil(nodeWidth / 32) : 8;
        domNode.style.setProperty('padding-right', `${rightPadding}px`, 'important');
        setTimeout(() => {
          domNode.style.overflow = initialOverflow;
          domNode.style.textOverflow = initialTextOverflow;
          domNode.style.removeProperty('padding-right');
        }, 4000);
      }
      if (domNode.hasAttribute?.('data-pptx-hide') && domNode.style) {
        domNode.style.visibility = 'hidden';
        setTimeout(() => {
          domNode.style.visibility = 'initial';
        }, 4000);
      }
      // Handle data-pptx-only attribute (elements that should only appear in PowerPoint)
      if (domNode.hasAttribute?.('data-pptx-only') && domNode.style) {
        // Save original display value
        const originalDisplay = domNode.style.display;
        // Make sure it's visible for the PowerPoint export
        domNode.style.display = 'block';
        setTimeout(() => {
          // Restore original display value
          domNode.style.display = originalDisplay;
        }, 4000);
      }
      // Check for hide-from-export class
      if (domNode.classList?.contains('hide-from-export')) {
        domNode.style.display = 'none';
        setTimeout(() => {
          domNode.style.display = '';
        }, 4000);
      }
      return true;
    },
  });
  for (let i = restores.length - 1; i >= 0; i--) restores[i]();
  const rect = element.getBoundingClientRect();
  const { width, height } = await getSizeOfBase64Image(image).catch(() => ({ width: rect.width, height: rect.height }));
  return { image, width, height };
};
const hideUnwantedElements = async () => {
  const elements = document.querySelectorAll<HTMLElement>('[data-pptx-click]');
  for (const element of elements) {
    if (element.getAttribute('data-pptx-click') === 'false') continue;
    element.click();
    await wait(100);
  }
  await wait(500);
};

/**
 * Combines two base64-encoded images vertically with a specified spacing in between.
 * It can take 20ms for one normal sized image, so it's 10 times faster than making screenshot
 *
 * @param image1Base64 - The first image as a base64-encoded PNG string.
 * @param image2Base64 - The second image as a base64-encoded PNG string.
 * @param spacing - The spacing (in pixels) between the two images.
 * @returns A promise that resolves to a base64-encoded PNG string of the combined image.
 *
 * @example
 * const newImage = await combineImages('data:image/png;base64,...', 'data:image/png;base64,...', 10)
 */
const joinImagesVertically = (img1Data: ImageData, img2Data: ImageData, spacing: number): Promise<ImageData> => {
  const SCALE_FACTOR = 3; // Scale factor to improve resolution
  return new Promise((resolve, reject) => {
    // Create an off-screen canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return reject('Unable to get canvas context');
    }

    // Multiply the dimensions by the scale factor to improve resolution
    const scaledWidth = Math.max(img1Data.width, img2Data.width) * SCALE_FACTOR;
    const scaledHeight = (img1Data.height + img2Data.height + spacing) * SCALE_FACTOR;

    // Set the canvas size at a higher resolution
    canvas.width = scaledWidth;
    canvas.height = scaledHeight;

    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Scale the context to handle the higher resolution drawing
    ctx.scale(SCALE_FACTOR, SCALE_FACTOR);

    // Create image elements
    const img1 = new Image();
    const img2 = new Image();

    img1.src = img1Data.image;

    // Wait for both images to load
    img1.onload = () => {
      img2.src = img2Data.image;

      img2.onload = () => {
        // Draw the first image, centered if necessary
        const img1X = (Math.max(img1Data.width, img2Data.width) - img1Data.width) / 2;
        ctx.drawImage(img1, img1X, 0, img1Data.width, img1Data.height);

        // Draw the second image, centered and below the first one
        const img2X = (Math.max(img1Data.width, img2Data.width) - img2Data.width) / 2;
        ctx.drawImage(img2, img2X, img1Data.height + spacing, img2Data.width, img2Data.height);

        // Export the combined image as a base64 string at original resolution
        const combinedImageBase64 = canvas.toDataURL('image/png');
        resolve({
          image: combinedImageBase64,
          width: Math.max(img1Data.width, img2Data.width),
          height: img1Data.height + img2Data.height + spacing,
        });
      };

      img2.onerror = (err) => {
        reject(`Error loading second image: ${err}`);
      };
    };

    img1.onerror = (err) => {
      reject(`Error loading first image: ${err}`);
    };
  });
};

const getSizeOfBase64Image = (base64: string): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = (e) => {
      console.error('Error loading base64 image for size calculation', e);
      reject(e);
    };
    img.src = base64;
  });
};
